import { trackSigninSsoErrorError } from "@faire/web-api/events/signin/error/ssoError";
import { SocialSignOnProvider } from "@faire/web-api/indigofair/data/SocialSignOnProvider";
import { getDocumentOrThrow } from "@faire/web/common/globals/getDocument";
import { getWindowOrThrow } from "@faire/web/common/globals/getWindow";
import { useStrictLocalization } from "@faire/web/common/localization";
import { logError } from "@faire/web/common/logging";
import React, { FC, useCallback, useEffect } from "react";

import { BaseSocialLoginButton } from "@faire/retailer/components/SocialSignOnButtons/BaseSocialLoginButton";
import { extractMessage } from "@faire/retailer/components/SocialSignOnButtons/errorMessages";
import {
  ISocialSignOnButtonProps,
  useHandleSSOSuccess,
} from "@faire/retailer/components/SocialSignOnButtons/useHandleSSOSuccess";
import {
  initializeAppleLogin,
  isAppleLoginInitialized,
} from "@faire/retailer/initializers/initializeAppleLogin";
import { useSSOErrorStore } from "@faire/retailer/stores/domain/SSO/useSSOErrorStore";
import { useSSOStore } from "@faire/retailer/stores/domain/SSO/useSSOStore";

import { useScriptCheck } from "./useScriptCheck";

export interface AppleResponseUser {
  email: string;
  name: {
    firstName: string;
    lastName: string;
  };
}

interface AppleResponse {
  authorization: {
    state: string;
    code: string;
    id_token: string;
  };
  user: AppleResponseUser;
  error?: string;
}

type AppleLoginEvent = Event & { detail?: AppleResponse };

const PROVIDER = SocialSignOnProvider.APPLE;
const LISTENER_PREFIX = "AppleIDSignInOn";
const SUCCESS = "Success";
const FAILURE = "Failure";

export const AppleLoginButton: FC<ISocialSignOnButtonProps> = (props) => {
  const { strictLocalize } = useStrictLocalization();
  const { isScriptLoading, hasScriptError } = useScriptCheck({
    isScriptLoaded: isAppleLoginInitialized,
    loadScript: () => initializeAppleLogin(setSSOLocalizedErrorMessage),
    provider: PROVIDER,
  });
  const { isOnSignIn, sourceId } = props;

  const handleSSOSuccess = useHandleSSOSuccess({
    ...props,
    provider: PROVIDER,
  });

  const {
    errorMessage,
    setLocalizedErrorMessage: setSSOLocalizedErrorMessage,
    setErrorMessage,
    resetErrorMessage,
  } = useSSOErrorStore();

  const { recordSSOButtonClick, userClickedSSOButton } = useSSOStore();

  const onSuccess = useCallback(
    async (event: AppleLoginEvent) => {
      const response = event.detail;
      const id_token = response?.authorization.id_token ?? "";
      handleSSOSuccess(id_token, response?.user);
    },
    [handleSSOSuccess]
  );

  const onFailure = useCallback(
    (event: AppleLoginEvent) => {
      const response = event.detail;
      logError(response, {
        tags: {
          signInType: "SSO",
          provider: PROVIDER,
          code: response?.error,
        },
      });
      const code = response?.error ?? "";
      const errorMessage = extractMessage(
        {
          provider: PROVIDER,
          code,
        },
        strictLocalize
      );
      setErrorMessage(errorMessage);
      trackSigninSsoErrorError(PROVIDER, sourceId);
    },
    [setErrorMessage, sourceId, strictLocalize]
  );

  useEffect(() => {
    try {
      getDocumentOrThrow().addEventListener(
        `${LISTENER_PREFIX}${SUCCESS}`,
        onSuccess
      );
      getDocumentOrThrow().addEventListener(
        `${LISTENER_PREFIX}${FAILURE}`,
        onFailure
      );
    } catch (error) {
      logError(error, {
        tags: {
          signInType: "SSO",
          provider: PROVIDER,
          code: "dom_function_injection_failed",
        },
      });
    }

    return () => {
      try {
        getDocumentOrThrow().removeEventListener(
          `${LISTENER_PREFIX}${SUCCESS}`,
          onSuccess
        );
        getDocumentOrThrow().removeEventListener(
          `${LISTENER_PREFIX}${FAILURE}`,
          onFailure
        );
      } catch (e) {
        logError(e);
      }
    };
  }, [onFailure, onSuccess]);

  if (hasScriptError) {
    return (
      <BaseSocialLoginButton
        onClick={() =>
          setErrorMessage(
            strictLocalize({
              defaultMessage:
                "Apple script is blocked. Unblock and refresh to use Apple sign in.",
              description: {
                text: "Error message for unable to use Apple ID log in",
              },
            })
          )
        }
        socialProvider={PROVIDER}
        isOnSignIn={isOnSignIn}
        sourceId={sourceId}
      />
    );
  }

  if (!userClickedSSOButton && errorMessage) {
    return (
      <BaseSocialLoginButton
        onClick={recordSSOButtonClick}
        socialProvider={PROVIDER}
        isOnSignIn={isOnSignIn}
        sourceId={sourceId}
      />
    );
  }

  const onClick = () => {
    try {
      if (errorMessage) {
        resetErrorMessage();
      }
      const windowObj = getWindowOrThrow();
      if (
        !isScriptLoading &&
        windowObj?.AppleID?.auth &&
        typeof windowObj?.AppleID?.auth.signIn === "function"
      ) {
        AppleID.auth.signIn();
      }
    } catch (error) {
      logError(error, {
        tags: {
          signInType: "SSO",
          provider: PROVIDER,
          code: "auth_signIn_failed",
        },
      });
    }
  };

  return (
    <BaseSocialLoginButton
      disabled={props.disabled}
      onClick={onClick}
      socialProvider={PROVIDER}
      loading={isScriptLoading}
      isOnSignIn={isOnSignIn}
      sourceId={sourceId}
    />
  );
};
